import type { ShortcutHelpGroup, ShortcutHelpItem } from '@workspace/backoffice/components/keyboard-shortcuts-menu';

/** Touches des raccourcis clavier de Deals (une seule touche, sans modificateur) */
export const shortcutKeys = {
    nouveauDevis: 'n',
    recherche: 'r',
    pilotage: 'p',
    appels: 'a',
    modifierDevis: 'm',
    souscrireDevis: 's',
    conseiller: 'c',
    lexique: '?',
} as const;

/** Ce à quoi l'utilisateur a accès : conditionne l'activation du raccourci et son affichage dans le lexique */
export interface ShortcutAccess {
    /** Accès à la page Pilotage */
    canSeePilotage: boolean;
    /** Liens de tarification télévente disponibles */
    hasTeleventeLinks: boolean;
}

interface ShortcutDefinition {
    key: string;
    label: string;
    hint?: string | ((access: ShortcutAccess) => string);
    /** Sans règle, le raccourci est accessible à tous */
    isAvailable?: (access: ShortcutAccess) => boolean;
}

const globalShortcuts: ShortcutDefinition[] = [
    {
        key: shortcutKeys.nouveauDevis,
        label: 'Nouveau devis',
        hint: ({ hasTeleventeLinks }) => (hasTeleventeLinks ? 'Tarification télévente 2 roues, dans un nouvel onglet' : 'Ouvre la page Nouveau devis'),
    },
    { key: shortcutKeys.recherche, label: 'Rechercher un client', hint: 'Popup de recherche dans Deals (immat, n° devis, nom, email, tél...)' },
    { key: shortcutKeys.appels, label: 'Appels' },
    { key: shortcutKeys.pilotage, label: 'Pilotage', isAvailable: ({ canSeePilotage }) => canSeePilotage },
    { key: shortcutKeys.conseiller, label: 'Espace conseiller', hint: 'Accueil de l’espace conseiller, dans un nouvel onglet' },
    { key: shortcutKeys.lexique, label: 'Afficher ce lexique' },
];

const quoteShortcuts: ShortcutDefinition[] = [
    { key: shortcutKeys.modifierDevis, label: 'Modifier le devis', hint: 'Si le devis est modifiable' },
    { key: shortcutKeys.souscrireDevis, label: 'Souscrire le devis', hint: 'Si le devis est souscriptible' },
];

const allShortcuts = [...globalShortcuts, ...quoteShortcuts];

/** À utiliser pour activer un raccourci : le lexique s'appuie sur la même règle */
export function isShortcutAvailable(key: string, access: ShortcutAccess): boolean {
    const definition = allShortcuts.find((shortcut) => shortcut.key === key);
    return definition?.isAvailable?.(access) ?? true;
}

function toHelpItems(definitions: ShortcutDefinition[], access: ShortcutAccess): ShortcutHelpItem[] {
    return definitions
        .filter((definition) => isShortcutAvailable(definition.key, access))
        .map((definition) => ({
            keys: [definition.key.toUpperCase()],
            label: definition.label,
            hint: typeof definition.hint === 'function' ? definition.hint(access) : definition.hint,
        }));
}

/** Lexique affiché dans la fenêtre d'aide, limité aux raccourcis auxquels l'utilisateur a accès */
export function getShortcutGroups(access: ShortcutAccess): ShortcutHelpGroup[] {
    return [
        { title: 'Partout', items: toHelpItems(globalShortcuts, access) },
        { title: 'Fiche devis', items: toHelpItems(quoteShortcuts, access) },
    ].filter((group) => group.items.length > 0);
}
